import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/App';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import Layout from '@/components/Layout';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { professionalCategories, companySectors, listingPriorities } from '@/lib/constants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EditListing = () => {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    sector: '',
    listing_type: '',
    priority: 'media'
  });

  // Use imported constants
  const categories = professionalCategories;
  const sectors = companySectors;
  const priorities = listingPriorities;

  useEffect(() => {
    fetchListing();
  }, [id]);

  const fetchListing = async () => {
    try {
      const response = await axios.get(`${API}/listings/${id}`);
      const listing = response.data;
      
      // Verifica che l'utente sia il proprietario
      if (listing.user_id !== user.id) {
        toast.error('Non sei autorizzato a modificare questo annuncio');
        navigate('/listings');
        return;
      }
      
      setFormData({
        title: listing.title,
        description: listing.description,
        category: listing.category,
        sector: listing.sector,
        listing_type: listing.listing_type,
        priority: listing.priority || 'media'
      });
    } catch (error) {
      console.error('Error fetching listing:', error);
      toast.error('Errore nel caricamento dell\'annuncio');
      navigate('/listings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description || !formData.category || !formData.sector) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    setSaving(true);
    try {
      const dataToSend = { ...formData };
      // Only send priority for company users
      if (user.user_type !== 'company') {
        delete dataToSend.priority;
      }
      await axios.put(`${API}/listings/${id}`, dataToSend);
      toast.success('Annuncio aggiornato con successo!');
      navigate(`/listings/${id}`);
    } catch (error) {
      console.error('Error updating listing:', error);
      toast.error('Errore nell\'aggiornamento dell\'annuncio');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-6 py-8 mt-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="mb-6 text-slate-600 hover:text-[#344B71]"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Indietro
          </Button>

          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-playfair font-bold text-[#344B71] mb-2" data-testid="edit-listing-title">
              Modifica Annuncio
            </h1>
            <p className="text-slate-600">
              Aggiorna le informazioni del tuo annuncio
            </p>
          </div>

          <Card className="p-8 bg-white">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="listing_type" className="text-sm font-medium text-slate-700">
                  Tipo di Annuncio *
                </Label>
                <Select 
                  value={formData.listing_type} 
                  onValueChange={(value) => setFormData({...formData, listing_type: value})}
                >
                  <SelectTrigger className="mt-1 h-12" data-testid="listing-type-select">
                    <SelectValue placeholder="Seleziona tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="offer">Offerta di Servizio</SelectItem>
                    <SelectItem value="request">Richiesta di Servizio</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="title" className="text-sm font-medium text-slate-700">
                  Titolo dell'Annuncio *
                </Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="es. Consulenza strategica per PMI"
                  required
                  className="mt-1 h-12"
                  data-testid="title-input"
                />
              </div>

              <div>
                <Label htmlFor="category" className="text-sm font-medium text-slate-700">
                  Categoria Professionale * (Non modificabile)
                </Label>
                <Input
                  id="category"
                  value={formData.category}
                  disabled
                  className="mt-1 h-12 bg-slate-100 cursor-not-allowed"
                  data-testid="category-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  La categoria non può essere modificata. Per cambiare categoria, crea un nuovo annuncio.
                </p>
              </div>

              <div>
                <Label htmlFor="sector" className="text-sm font-medium text-slate-700">
                  Settore *
                </Label>
                <Select 
                  value={formData.sector} 
                  onValueChange={(value) => setFormData({...formData, sector: value})}
                >
                  <SelectTrigger className="mt-1 h-12" data-testid="sector-select">
                    <SelectValue placeholder="Seleziona settore" />
                  </SelectTrigger>
                  <SelectContent>
                    {sectors.map((sector) => (
                      <SelectItem key={sector} value={sector}>{sector}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="description" className="text-sm font-medium text-slate-700">
                  Descrizione Dettagliata *
                </Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={8}
                  placeholder="Descrivi in dettaglio il servizio offerto o richiesto, le competenze necessarie, le modalità di collaborazione..."
                  required
                  className="mt-1"
                  data-testid="description-input"
                />
              </div>

              {/* Priority selector - only for companies */}
              {user.user_type === 'company' && (
                <div>
                  <Label htmlFor="priority" className="text-sm font-medium text-slate-700">
                    Priorità dell'Annuncio
                  </Label>
                  <p className="text-xs text-slate-500 mb-2">
                    Seleziona la priorità per evidenziare l'urgenza della tua richiesta
                  </p>
                  <Select 
                    value={formData.priority} 
                    onValueChange={(value) => setFormData({...formData, priority: value})}
                  >
                    <SelectTrigger className="mt-1 h-12" data-testid="priority-select">
                      <SelectValue placeholder="Seleziona priorità" />
                    </SelectTrigger>
                    <SelectContent>
                      {priorities.map((p) => (
                        <SelectItem key={p.value} value={p.value}>
                          <span className={p.color}>{p.label}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex justify-end space-x-4 pt-6 border-t border-slate-200">
                <Button
                  type="button"
                  onClick={() => navigate(-1)}
                  variant="outline"
                  className="border-slate-300 text-slate-700 hover:bg-slate-100 rounded-xl"
                  data-testid="cancel-button"
                >
                  Annulla
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-[#344B71] hover:bg-[#344B71] text-white px-8 rounded-xl shadow-lg hover:shadow-xl transition-all"
                  data-testid="save-button"
                >
                  {saving ? 'Salvataggio...' : 'Salva Modifiche'}
                </Button>
              </div>
            </form>
          </Card>
        </motion.div>
      </div>
    </Layout>
  );
};

export default EditListing;
